// src/features/records/components/RecordDropdownMenu.js
import { useEffect, useRef, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Edit2, Trash2, MoreHorizontal, UserPlus } from 'lucide-react';
import RecordDetailContext from '../../contexts/RecordDetailContext';
import accessControlService from '../../services/accessControlService';
import styles from './RecordDropdownMenu.module.css';

const RecordDropdownMenu = ({ 
  isOpen, 
  onToggle, 
  onEdit, 
  onDelete,
  onShare,
  record = null,
  className = '' 
}) => {
  const { t } = useTranslation();
  const menuRef = useRef(null);
  const buttonRef = useRef(null);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Try to get context handlers
  const context = useContext(RecordDetailContext);
  
  // Use context handlers if available and no props provided
  const handleEdit = onEdit || (context && context.editRecord ? () => context.editRecord(record) : null);
  const handleDelete = onDelete || (context && context.deleteRecord ? () => context.deleteRecord(record) : null);
  const handleShare = onShare || (context && context.shareRecord ? () => context.shareRecord(record) : null);

  // Check if user is admin for this record
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (record) {
        const adminStatus = await accessControlService.isUserAdmin(record, { forceRefresh: true });
        setIsAdmin(adminStatus);
      }
    };
    
    checkAdminStatus();
  }, [record]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current && 
        !menuRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        onToggle(false);
      }
    };

    const handleScroll = () => {
      if (isOpen) {
        onToggle(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, { passive: true });
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isOpen, onToggle]);

  const handleEditClick = (e) => {
    e.stopPropagation();
    onToggle(false);
    if (handleEdit) {
      handleEdit();
    }
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    onToggle(false);
    if (handleDelete) {
      handleDelete();
    }
  };

  const handleShareClick = (e) => {
    e.stopPropagation();
    onToggle(false);
    if (handleShare) {
      handleShare();
    }
  };

  const handleButtonClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onToggle(!isOpen);
  };

  const handleButtonTouchEnd = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onToggle(!isOpen);
  };

  return (
    <div className={`${styles.dropdownContainer} ${className}`}>
      <button 
        ref={buttonRef}
        onClick={handleButtonClick}
        onTouchEnd={handleButtonTouchEnd}
        className={`${styles.menuButton} ${isOpen ? styles.menuButtonActive : ''}`}
        aria-label={t('common.more_options', 'More options')}
        aria-expanded={isOpen}
        aria-haspopup="true"
        type="button"
      >
        <MoreHorizontal size={20} />
      </button>
      
      <div 
        ref={menuRef}
        className={`${styles.dropdownMenu} ${isOpen ? styles.dropdownMenuVisible : ''}`}
        role="menu"
        aria-hidden={!isOpen}
      >
        {isAdmin && (
          <button 
            onClick={handleShareClick} 
            className={styles.menuItem}
            role="menuitem"
            tabIndex={isOpen ? 0 : -1}
          >
            <UserPlus size={20} />
            {t('common.share', 'Share')}
          </button>
        )}
        <button 
          onClick={handleEditClick} 
          className={styles.menuItem}
          role="menuitem"
          tabIndex={isOpen ? 0 : -1}
        >
          <Edit2 size={20} />
          {t('common.edit', 'Edit')}
        </button>
        <button 
          onClick={handleDeleteClick} 
          className={`${styles.menuItem} ${styles.deleteItem}`}
          role="menuitem"
          tabIndex={isOpen ? 0 : -1}
        >
          <Trash2 size={20} />
          {t('records.actions.delete', 'Delete')}
        </button>
      </div>
    </div>
  );
};

export default RecordDropdownMenu;
